import React, { useState, useEffect } from 'react';
import CustomInput from '../../components/customInput/CustomInput';
import Button from '../../components/button';
import './CreateRole.css';
import Form from 'react-bootstrap/Form';
import Arrow from '../../static/image/arrow.png';
import { useDispatch, useSelector } from 'react-redux';
import { createRole, updateRole } from '../../redux/roleSlice';
import { EDIT_MODEL_CREATE, EDIT_MODEL_VIEW, EDIT_MODEL_EDIT, ERROR_INPUT_REQUIRED, ERROR_PERMISSION_NOT_CHOICE, MENU_CONFIG } from '../../utils/constant';
import CommonToast from '../../components/toast/CommonToast';
import { isEmpty } from '../../utils/commonUtil';

// Initialize form data
const getInitialFormData = () => ({
  roleId: null,
  roleName: '',
  menuSections: Object.keys(MENU_CONFIG).reduce((acc, menuId) => {
    const menu = MENU_CONFIG[menuId];
    if (menu.subMenus) {
      acc[menuId] = {
        isExpanded: true,
        isChecked: false
      };
    }
    return acc;
  }, {}),
  menuAccess: Object.keys(MENU_CONFIG).reduce((acc, menuId) => {
    if (!MENU_CONFIG[menuId].subMenus) {
      acc[menuId] = '';
    }
    return acc;
  }, {})
});

function CreateRole({ onCancel, onRoleCreated, editMode, role }) {
  const [formData, setFormData] = useState(getInitialFormData());
  const dispatch = useDispatch();
  const [formError, setFormError] = useState({
    roleName: undefined,
    permissions: undefined
  });
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastIsError, setToastIsError] = useState(false);

  // Update form data when role changes
  useEffect(() => {
    if (role && editMode !== EDIT_MODEL_CREATE) {
      const newData = getInitialFormData();
      newData.roleId = role.roleId;
      newData.roleName = role.roleName;

      try {
        const menuList = role.menuList ? JSON.parse(role.menuList) : [];
        menuList.forEach(menu => {
          const menuConfig = MENU_CONFIG[menu.menuId];
          if (menuConfig) {
            if (menuConfig.subMenus) {
              const sectionKey = menu.menuId;
              newData.menuSections[sectionKey].isChecked = true;
            } else {
              newData.menuAccess[menu.menuId] = getAccessLevel(menu.menuPermission);
            }
          }
        });
      } catch (error) {
        console.error('Error parsing menuList:', error);
      }

      setFormData(newData);
    }
  }, [role, editMode]);

  // Reset the form when the edit mode changes
  useEffect(() => {
    if (editMode === EDIT_MODEL_CREATE) {
      resetForm();
    }
  }, [editMode]);

  // Reset form data
  const resetForm = () => {
    setFormData(getInitialFormData());
    setFormError({
      roleName: undefined,
      permissions: undefined
    });
  };

  // Get the access level based on the permission
  const getAccessLevel = (permission) => {
    switch (permission) {
      case 'R':
        return 'View';
      case 'RW':
        return 'View and Edit';
      case 'RWX':
        return 'Full access';
      default:
        return '';
    }
  };

  // Get the permission level based on the access
  const getPermissionLevel = (access) => {
    switch (access) {
      case 'View':
        return 'R';
      case 'View and Edit':
        return 'RW';
      case 'Full access':
        return 'RWX';
      default:
        return '';
    }
  };

  // Toggle the expansion state of a section
  const toggleSection = (sectionId) => {
    setFormData(prev => ({
      ...prev,
      menuSections: {
        ...prev.menuSections,
        [sectionId]: {
          ...prev.menuSections[sectionId],
          isExpanded: !prev.menuSections[sectionId].isExpanded
        }
      }
    }));
  };

  // Handle the change of the section checkbox
  const handleSectionCheckboxChange = (sectionId, menuConfig) => {
    return (e) => {
      const isChecked = e.target.checked;
      setFormData(prev => {
        const newData = { ...prev };
        newData.menuSections[sectionId].isChecked = isChecked;
        if (isChecked) {
          menuConfig.subMenus.forEach(subMenuId => {
            newData.menuAccess[subMenuId] = 'View';
          });
        } else {
          menuConfig.subMenus.forEach(subMenuId => {
            newData.menuAccess[subMenuId] = '';
          });
        }
        return newData;
      });
      setFormError(prev => ({ ...prev, permissions: undefined }));
    };
  };

  // Handle the change of the role name
  const handleRoleNameChange = (e) => {
    setFormData(prev => ({
      ...prev,
      roleName: e.target.value
    }));
    setFormError(prev => ({ ...prev, roleName: undefined }));
  };

  // Handle form submission
  const handleSubmit = () => {
    const newError = {
      roleName: undefined,
      permissions: undefined
    };

    newError.roleName = isEmpty(formData.roleName) ? ERROR_INPUT_REQUIRED : undefined;

    const hasPermission = Object.values(formData.menuAccess).some(access => access);
    const hasSectionChecked = Object.values(formData.menuSections).some(section => section.isChecked);
    if (!hasSectionChecked && !hasPermission) {
      newError.permissions = ERROR_PERMISSION_NOT_CHOICE;
    }

    setFormError(newError);

    if (newError.roleName || newError.permissions) {
      return;
    }

    const menuList = [];
    Object.entries(formData.menuSections).forEach(([sectionId, section]) => {
      if (section.isChecked) {
        const menuConfig = MENU_CONFIG[sectionId];
        menuList.push({
          menuId: sectionId,
          menuName: menuConfig.name,
          menuPermission: getPermissionLevel('View'),
          permissionId: []
        });
      }
    });

    Object.keys(formData.menuAccess).forEach(menuId => {
      const access = formData.menuAccess[menuId];
      if (access) {
        const menuConfig = MENU_CONFIG[menuId];
        if (menuConfig) {
          menuList.push({
            menuId: menuId,
            menuName: menuConfig.name,
            menuPermission: getPermissionLevel(access),
            permissionId: menuConfig.accessMap[access]
          });
        }
      }
    });

    const payload = {
      roleName: formData.roleName,
      isAutoInherit: false,
      menuList: JSON.stringify(menuList)
    };

    if (editMode === EDIT_MODEL_EDIT) {
      payload.roleId = formData.roleId;
      dispatch(updateRole(payload))
        .unwrap()
        .then(() => {
          setToastShow(true);
          setToastMessage('Role updated successfully');
          setToastIsError(false);
          setTimeout(() => {
            if (onRoleCreated) {
              onRoleCreated();
            }
            onCancel();
          }, 1000);
        })
        .catch((error) => {
          setToastShow(true);
          const errorMessage = error.message;
          setToastMessage(errorMessage);
          setToastIsError(true);
        });
    } else {
      dispatch(createRole(payload))
        .unwrap()
        .then(() => {
          setToastShow(true);
          setToastMessage('Role created successfully');
          setToastIsError(false);
          setTimeout(() => {
            if (onRoleCreated) {
              onRoleCreated();
            }
            onCancel();
          }, 1000);
        })
        .catch((error) => {
          setToastShow(true);
          const errorMessage = error.message;
          setToastMessage(errorMessage);
          setToastIsError(true);
        });
    }
  };

  // Handle the change of the menu checkbox
  const handleMenuCheckboxChange = (menuId) => {
    return (e) => {
      const isChecked = e.target.checked;
      setFormData(prev => {
        const newData = { ...prev };
        const sectionId = Object.keys(MENU_CONFIG).find(key => MENU_CONFIG[key].subMenus?.includes(menuId));
        if (sectionId) {
          if (isChecked) {
            newData.menuSections[sectionId].isChecked = true;
          } else {
            // Check if all sub-menus are unchecked
            const allSubMenusUnchecked = MENU_CONFIG[sectionId].subMenus.every(subMenuId => !newData.menuAccess[subMenuId]);
            if (allSubMenusUnchecked) {
              newData.menuSections[sectionId].isChecked = false;
            }
          }
        }
        if (isChecked) {
          newData.menuAccess[menuId] = 'View';
        } else {
          newData.menuAccess[menuId] = '';
        }
        return newData;
      });
      setFormError(prev => ({ ...prev, permissions: undefined }));
    };
  };

  // Handle the change of the menu access level
  const handleMenuAccessChange = (menuId) => {
    return (e) => {
      setFormData(prev => {
        const newData = { ...prev };
        const sectionId = Object.keys(MENU_CONFIG).find(key => MENU_CONFIG[key].subMenus?.includes(menuId));
        if (sectionId) {
          newData.menuSections[sectionId].isChecked = true;
        }
        newData.menuAccess[menuId] = e.target.value;
        return newData;
      });
      setFormError(prev => ({ ...prev, permissions: undefined }));
    };
  };

  // Render menu items
  const renderMenuItems = (menuConfig, sectionId) => {
    return menuConfig.subMenus.map(menuId => {
      const isExcelUploadLog = menuId === "030300";
      return (
        <div key={menuId} className='administration-item'>
          <Form.Check
            type="checkbox"
            label={MENU_CONFIG[menuId].name}
            className="custom-form-check"
            checked={!!formData.menuAccess[menuId]}
            onChange={handleMenuCheckboxChange(menuId)}
            disabled={editMode === EDIT_MODEL_VIEW}
          />
          <Form.Check
            type="radio"
            label="View"
            name={`${menuId}Access`}
            value="View"
            checked={formData.menuAccess[menuId] === 'View'}
            onChange={handleMenuAccessChange(menuId)}
            className='right-20'
            disabled={editMode === EDIT_MODEL_VIEW}
          />
          {!isExcelUploadLog && (
            <>
              <Form.Check
                type="radio"
                label="View and Edit"
                name={`${menuId}Access`}
                value="View and Edit"
                checked={formData.menuAccess[menuId] === 'View and Edit'}
                onChange={handleMenuAccessChange(menuId)}
                className='right-20'
                disabled={editMode === EDIT_MODEL_VIEW}
              />
              <Form.Check
                type="radio"
                label="Full access"
                name={`${menuId}Access`}
                value="Full access"
                checked={formData.menuAccess[menuId] === 'Full access'}
                onChange={handleMenuAccessChange(menuId)}
                className='right-20'
                disabled={editMode === EDIT_MODEL_VIEW}
              />
            </>
          )}
        </div>
      );
    });
  };

  return (
    <div className='create-role-container'>
      <div style={{ padding: '50px' }}>
        <div className="form-grid">
          <div style={{ fontSize: '24px' }}>
            {editMode === EDIT_MODEL_EDIT ? 'Edit Role' :
              editMode === EDIT_MODEL_VIEW ? 'View Role' : 'Create Role'}
          </div>
          <div className="form-row">
            <span className="form-label">Role Name</span>
            <div className="form-control">
              <CustomInput
                value={formData.roleName}
                onChange={handleRoleNameChange}
                placeholder="Enter Role Name"
                style={{ width: '300px', fontSize: '14px' }}
                disabled={editMode !== EDIT_MODEL_CREATE}
                error={formError.roleName}
              />
            </div>
          </div>
          <div className="form-row permissions-row">
            <span className="form-label">Permissions</span>
            <div className="form-control">
              <div className="permissions-box" style={{ padding: '30px' }}>
                <Form.Group>
                  {Object.entries(MENU_CONFIG).map(([menuId, menuConfig]) => {
                    if (menuConfig.subMenus) {
                      return (
                        <div key={menuId} style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex' }}>
                            <Form.Check
                              type="checkbox"
                              label=""
                              checked={formData.menuSections[menuId].isChecked}
                              onChange={handleSectionCheckboxChange(menuId, menuConfig)}
                              disabled={editMode === EDIT_MODEL_VIEW}
                            />
                            <img
                              src={Arrow}
                              alt="arrow"
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleSection(menuId)}
                            />
                            <span
                              style={{ marginLeft: '5px', cursor: 'pointer' }}
                              onClick={() => toggleSection(menuId)}
                            >
                              {menuConfig.name}
                            </span>
                          </div>
                          {formData.menuSections[menuId].isExpanded && renderMenuItems(menuConfig, menuId)}
                        </div>
                      );
                    }
                    return null;
                  })}
                </Form.Group>
              </div>
              {formError.permissions && <div style={{ color: 'red', marginTop: '10px' }}>{formError.permissions}</div>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {editMode !== EDIT_MODEL_VIEW && (
            <Button className='create-user-btn' onClick={handleSubmit}>SAVE</Button>
          )}
          <Button
            className='create-user-btn'
            style={{ marginLeft: editMode !== EDIT_MODEL_VIEW ? '40px' : '0' }}
            onClick={onCancel}
          >
            CANCEL
          </Button>
        </div>

        <CommonToast
          show={toastShow}
          message={toastMessage}
          onClose={() => setToastShow(false)}
          isError={toastIsError}
        />
      </div>
    </div>
  );
}

export default CreateRole;